/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx, cssMap } from '@compiled/react';
import React from 'react';
import type { RenderFn } from '@atlassian/forge-ui-types';
import PlatformHeading from '@atlaskit/heading';
import { useIsMacro } from '../../utils/useIsModule';
import { token } from '@atlaskit/tokens';

type HeadingTags = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span';
type Sizes = 'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'xsmall' | 'xxsmall';

const onlyAllowHeadingTag = (asProp: string | undefined): HeadingTags | undefined => {
	if (!asProp) {
		return undefined;
	}
	if (asProp && ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span'].includes(asProp)) {
		return asProp as HeadingTags;
	}
	throw Error(
		`${asProp} is an invalid heading tag. Please use one of h1, h2, h3, h4, h5, h6, div, span.`,
	);
};

const getSize = (asProp: HeadingTags | undefined) => {
	if (!asProp) {
		return 'xlarge';
	}
	const sizeMap: Record<string, Sizes> = {
		h1: 'xlarge',
		h2: 'large',
		h3: 'medium',
		h4: 'small',
		h5: 'xsmall',
		h6: 'xxsmall',
	};
	return sizeMap[asProp];
};

type _PlatformHeadingProps = React.ComponentProps<typeof PlatformHeading>;
export type PlatformHeadingProps = Omit<_PlatformHeadingProps, 'size'> & {
	size?: Sizes;
};

export type HeadingProps = Pick<
	PlatformHeadingProps,
	'size' | 'color' | 'as' | 'children' | 'id' | 'testId'
>;

// Heading wrapper to override styles in the case of a macro where size and as are provided (EXT-2828)
// Sizes differ slightly from sizes in editor (confluence/next/packages/content-renderer/src/renderers/StyledRenderer.tsx)
// xxlarge (token = 1.714em) (editor = does not exist)
// xlarge (token = 1.43em) (editor = 1.428em)
// large (token = 1.2em) (editor = 1.166em)
// medium (token = 1em) (editor = 1.142em)
// small (token = 0.857em) (editor = 1em)
// xsmall (token = 0.785em) (editor = 0.857em)
// xxsmall (token = 0.714em) (editor = 0.785em)

const headingStyles = cssMap({
	xxlarge: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			font: `${token('font.heading.xxlarge')} !important`,
		},
	},
	xlarge: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			font: `${token('font.heading.xlarge')} !important`,
		},
	},
	large: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			font: `${token('font.heading.large')} !important`,
		},
	},
	medium: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			font: `${token('font.heading.medium')} !important`,
		},
	},
	small: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			font: `${token('font.heading.small')} !important`,
		},
	},
	xsmall: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			font: `${token('font.heading.xsmall')} !important`,
		},
	},
	xxsmall: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			font: `${token('font.heading.xxsmall')} !important`,
		},
	},
});

/**
 * A heading is a typography component used to display text in different sizes and formats.
 */
export const Heading = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const { size, color, as, id, testId } = props.forgeDoc.props as HeadingProps;

	const effectiveSize = size ? size : getSize(as);
	const isMacro = useIsMacro();

	if (isMacro) {
		return (
			<div css={headingStyles[effectiveSize]}>
				<PlatformHeading
					size={effectiveSize}
					color={color}
					as={onlyAllowHeadingTag(as)}
					children={props.forgeDoc.children.map((c) => (
						<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
					))}
					id={id}
					testId={testId}
				/>
			</div>
		);
	}
	
	return (
		<PlatformHeading
			size={effectiveSize}
			color={color}
			as={onlyAllowHeadingTag(as)}
			children={props.forgeDoc.children.map((c) => (
				<React.Fragment key={c.key}>{props.render(c)}</React.Fragment>
			))}
			id={id}
			testId={testId}
		/>
	);
};
